import React, { useState, useEffect, useContext, useMemo, createContext } from "react";
import {
  Avatar,
  Badge,
  Flex,
  Tooltip,
  Button,
  Popconfirm,
  Popover,
  Space,
  Dropdown,
  Select,
  Modal,
  Typography,
  Empty,
  theme
} from "antd";
import {
  FilterOutlined,
  ReloadOutlined,
  DownOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  WarningFilled,
  BarChartOutlined,
  SwapOutlined,
  CopyOutlined,
  ExportOutlined,
  PlusOutlined
} from "@ant-design/icons";
import { Calendar, Views, momentLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "./ShiftPlanning.css";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Error, Success } from "../../../commons/Messages/Messages";
import { isAuthenticated } from "../../../commons/Auth/auth";
import AddShifts from "./ManageShifts/AddShifts";
import { SocketContext } from "../../../context/SocketContext";
import UpdateShift from "./ManageShifts/UpdateShifts";
import WorkloadDrawer from "./WorkloadDrawer";
import SwapRequestsDrawer from "./SwapRequestsDrawer";
import CopyWeekModal from "./CopyWeekModal";
import { exportICS, exportCSV, printShifts } from "./exportShifts";
import { authHeaders, saveWithConflictCheck, shiftApi } from "./shiftApi";
import { FaInfoCircle } from "react-icons/fa";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import PageToolbar from "../../../design-system/PageToolbar";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import { calendarMessages } from "./calendarMessages";
import {
  expandShift,
  getVisibleRange,
  getViewPeriod,
  computeWorkload,
  weekKey,
  isRecurringShift,
  getTimeZone,
  RECUR_FORMAT,
  DATE_KEY,
  DEFAULT_SHIFT_COLOR,
  DEFAULT_WORK_RULES
} from "../../../utils/shiftUtils";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

// react-big-calendar works in local time via momentLocalizer
const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);
const RULES_STORAGE_KEY = "shiftPlanning.workRules";
const { Text } = Typography;

const fullName = emp => `${emp?.firstName || ""} ${emp?.lastName || ""}`.trim();
const ids = list => (list || []).map(e => e?._id || e);
const isUnderstaffed = event =>
  !event.cancelled && event.minStaff > 0 && (event.employees || []).length < event.minStaff;

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Flag employees assigned to occurrences whose time ranges intersect.
 * Both conflicting occurrences get a `duplicateEmployees` entry for that employee.
 */
const detectOverlaps = events => {
  const byEmployee = {};
  events
    .filter(event => !event.cancelled)
    .forEach(event =>
      (event.employees || []).forEach(employee => {
        if (!byEmployee[employee._id]) byEmployee[employee._id] = [];
        byEmployee[employee._id].push(event);
      })
    );

  const conflicts = {}; // occurrence _id -> { employeeId -> [messages] }
  const addConflict = (event, employeeId, other, hours) => {
    conflicts[event._id] = conflicts[event._id] || {};
    (conflicts[event._id][employeeId] = conflicts[event._id][employeeId] || []).push(
      `Also in "${other.title}" ${moment(other.start).format("DD/MMM HH:mm")} – ${moment(
        other.end
      ).format("HH:mm")} (overlap ${hours.toFixed(1)}h)`
    );
  };

  Object.entries(byEmployee).forEach(([employeeId, list]) => {
    list.sort((a, b) => a.start - b.start);
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length && list[j].start < list[i].end; j++) {
        const a = list[i];
        const b = list[j];
        if (a._originalId === b._originalId) continue;
        const hours = (Math.min(a.end, b.end) - b.start) / 36e5;
        addConflict(a, employeeId, b, hours);
        addConflict(b, employeeId, a, hours);
      }
    }
  });

  return events.map(event => {
    const byEmp = conflicts[event._id];
    if (!byEmp) return event;
    return {
      ...event,
      duplicateEmployees: Object.entries(byEmp).map(([_id, messages]) => ({
        _id,
        shiftMessage: messages.join("\n")
      }))
    };
  });
};

const plannedHoursIn = (pool, employeeId, week) =>
  pool
    .filter(
      o =>
        !o.cancelled &&
        weekKey(o.start) === week &&
        (o.employees || []).some(e => e._id === employeeId)
    )
    .reduce((sum, o) => sum + (o.end - o.start) / 36e5, 0);

// ─────────────────────────────────────────────────────────────────────────────
// EVENT RENDERING (outside the page so the calendar does not remount events)
// ─────────────────────────────────────────────────────────────────────────────

const PlanningContext = createContext(null);

const SuggestionList = ({ event, replacing }) => {
  const { t, rankSuggestions, setDayStaff, setSeriesStaff } = useContext(PlanningContext);
  const suggestions = rankSuggestions(event).slice(0, 6);
  const recurring = isRecurringShift(event._shift);
  const dayIds = ids(event.employees);
  const seriesIds = ids(event._shift.employees);
  const swap = (list, newId) =>
    replacing ? list.map(id => (id === replacing._id ? newId : id)) : [...list, newId];

  if (suggestions.length === 0) {
    return <Text type="secondary">{t("Nobody free for this time")}</Text>;
  }
  return (
    <Flex vertical gap={6} style={{ minWidth: "min(240px, 80vw)" }}>
      {suggestions.map(({ employee, hours }) => (
        <Flex key={employee._id} justify="space-between" align="center" gap={8}>
          <span>
            {fullName(employee)}{" "}
            <Text type="secondary" style={{ fontSize: 12 }}>
              {hours.toFixed(1)}h {t("this week")}
            </Text>
          </span>
          <Space size={4}>
            <Popconfirm
              title={
                replacing
                  ? `${t("Replace")} ${fullName(replacing)} ${t("with")} ${fullName(employee)} ${t("on this day")}?`
                  : `${t("Add")} ${fullName(employee)} ${t("on this day")}?`
              }
              onConfirm={() => setDayStaff(event, swap(dayIds, employee._id))}
            >
              <Button size="small">{t("This day")}</Button>
            </Popconfirm>
            {recurring && (
              <Popconfirm
                title={
                  replacing
                    ? `${t("Replace")} ${fullName(replacing)} ${t("with")} ${fullName(employee)} ${t("on every day of")} "${event.title}"?`
                    : `${t("Add")} ${fullName(employee)} ${t("to every day of")} "${event.title}"?`
                }
                onConfirm={() => setSeriesStaff(event, swap(seriesIds, employee._id))}
              >
                <Button size="small" type="dashed">
                  {t("All days")}
                </Button>
              </Popconfirm>
            )}
          </Space>
        </Flex>
      ))}
    </Flex>
  );
};

// Stops clicks/drags inside an event from opening the edit modal or starting a drag
const stop = e => e.stopPropagation();

const EventComponent = ({ event }) => {
  const { t, view, findLeave, workIssues } = useContext(PlanningContext);
  const { token } = theme.useToken();
  const staffCount = (event.employees || []).length;
  const understaffed = isUnderstaffed(event);
  const hasWarnings =
    !event.cancelled &&
    ((event.duplicateEmployees || []).length > 0 ||
      !!workIssues[event._id] ||
      (event.employees || []).some(emp => findLeave(emp._id, event.start)));

  return (
    <div className="EventComponent">
      <div style={{ fontWeight: 600, fontSize: 12, lineHeight: 1.3 }}>
        {hasWarnings && (
          <WarningFilled style={{ color: token.colorWarningBorderHover, marginInlineEnd: 4 }} />
        )}
        {event.title}
      </div>

      <Flex gap={8} wrap="wrap" style={{ fontSize: 11, marginTop: 2, opacity: 0.95 }}>
        <span style={{ whiteSpace: "nowrap" }}>
          <ClockCircleOutlined style={{ fontSize: 10 }} /> {moment(event.start).format("HH:mm")} –{" "}
          {moment(event.end).format("HH:mm")}
        </span>
        {(event.minStaff > 0 || view !== Views.MONTH) && (
          <span className={understaffed ? "staffCount understaffed" : "staffCount"}>
            <TeamOutlined /> {staffCount}
            {event.minStaff > 0 && `/${event.minStaff}`}
          </span>
        )}
        {event.cancelled && <span>({t("Cancelled")})</span>}
      </Flex>

      {/* Day view: employee avatars with leave / overlap / working-time warnings */}
      {view === Views.DAY && !event.cancelled && (
        <Flex gap={6} align="center" wrap="wrap" style={{ marginTop: 6 }}>
          {(event.employees || []).map(employee => {
            const leave = findLeave(employee._id, event.start);
            const dupEntry = event?.duplicateEmployees?.find(e => e._id === employee._id);
            const issues = workIssues[event._id]?.[employee._id] || [];

            return (
              <Tooltip
                key={employee._id}
                title={
                  <div onMouseDown={stop} onClick={stop}>
                    <strong>{fullName(employee)}</strong>
                    {leave && (
                      <div style={{ marginTop: 4 }}>
                        <span style={{ color: token.colorErrorBorderHover }}>
                          {t("On")} {leave?.leaveType} {t("leave")}
                        </span>
                        <div style={{ margin: "4px 0" }}>{t("Replace with")}:</div>
                        <SuggestionList event={event} replacing={employee} />
                      </div>
                    )}
                    {dupEntry && (
                      <div
                        style={{ color: token.colorWarning, marginTop: 4, whiteSpace: "pre-line" }}
                      >
                        {dupEntry.shiftMessage}
                      </div>
                    )}
                    {issues.map((msg, i) => (
                      <div key={i} style={{ color: token.colorErrorBorder, marginTop: 4 }}>
                        {msg}
                      </div>
                    ))}
                  </div>
                }
              >
                <Flex
                  align="center"
                  style={{ marginTop: 4 }}
                  className={
                    leave ? "AbsentEmployee" : dupEntry || issues.length ? "DuplicateEmp" : ""
                  }
                >
                  <Badge
                    count={
                      leave ? (
                        <FaInfoCircle style={{ fontSize: 16, color: token.colorWarning }} />
                      ) : null
                    }
                  >
                    <Avatar size="large" src={employee?.picture?.url || null}>
                      {!employee?.picture?.url &&
                        employee?.firstName?.substring(0, 2).toUpperCase()}
                    </Avatar>
                  </Badge>
                </Flex>
              </Tooltip>
            );
          })}
          <Popover
            trigger="click"
            title={t("Add employee")}
            content={
              <div onMouseDown={stop} onClick={stop}>
                <SuggestionList event={event} />
              </div>
            }
          >
            <Button
              shape="circle"
              size="small"
              icon={<PlusOutlined />}
              onMouseDown={stop}
              onClick={stop}
              aria-label={t("Add employee")}
            />
          </Popover>
        </Flex>
      )}

      {/* Week view: avatar group */}
      {view === Views.WEEK && !event.cancelled && (
        <Avatar.Group
          size="small"
          style={{ marginTop: 4 }}
          max={{
            count: 4,
            style: {
              color: token.colorPrimary,
              backgroundColor: token.colorPrimaryBg,
              cursor: "pointer"
            },
            popover: { trigger: "hover" }
          }}
        >
          {(event.employees || []).map(employee => (
            <Avatar key={employee._id} size="small" src={employee?.picture?.url || null}>
              {!employee?.picture?.url && employee?.firstName?.substring(0, 2).toUpperCase()}
            </Avatar>
          ))}
        </Avatar.Group>
      )}
    </div>
  );
};

const calendarComponents = { event: EventComponent };

// ─────────────────────────────────────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────────────────────────────────────

export const ShiftPlanning = () => {
  const { t, i18n } = useTranslation("dashboard");
  const { token } = theme.useToken();
  const isMobile = useIsMobile();
  const socket = useContext(SocketContext);

  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [leavesArray, setLeavesArray] = useState([]);
  const [swapRequests, setSwapRequests] = useState([]);
  const [editTarget, setEditTarget] = useState(null);
  const [addState, setAddState] = useState({ open: false, initialValues: undefined });
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [calendarView, setCalendarView] = useState(isMobile ? Views.DAY : Views.MONTH);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [workloadOpen, setWorkloadOpen] = useState(false);
  const [requestsOpen, setRequestsOpen] = useState(false);
  const [copyWeekOpen, setCopyWeekOpen] = useState(false);
  const [actuals, setActuals] = useState({});
  const [workRules, setWorkRules] = useState(() => {
    try {
      return {
        ...DEFAULT_WORK_RULES,
        ...JSON.parse(localStorage.getItem(RULES_STORAGE_KEY) || "{}")
      };
    } catch {
      return DEFAULT_WORK_RULES;
    }
  });

  // ─── Data ───────────────────────────────────────────────────────────────

  const getAllShifts = async () => {
    try {
      const res = await shiftApi.get("");
      if (res.status === 200 && Array.isArray(res.data)) {
        setShifts(res.data);
      } else {
        Error(res.data?.errorMessage);
      }
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const getAllEmployees = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/employees/employee/creator/${isAuthenticated()?.serviceProviderId}`,
        { headers: authHeaders() }
      );
      if (res.status === 200 || res.status === 304) {
        setEmployees(res.data);
      } else {
        Error(res.data.errorMessage);
      }
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const getAllLeaves = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/leaves`, {
        headers: authHeaders()
      });
      if (res.status === 200 || res.status === 304) {
        setLeavesArray(res.data);
      } else {
        Error(res.data.errorMessage);
      }
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const getSwapRequests = async () => {
    try {
      const res = await shiftApi.get("/swap-requests");
      setSwapRequests(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  // ─── Lifecycle ──────────────────────────────────────────────────────────

  const refreshData = () => {
    getAllShifts();
    getAllEmployees();
    getAllLeaves();
    getSwapRequests();
  };

  useEffect(() => {
    refreshData();
  }, []);

  useEffect(() => {
    if (!socket) return undefined;
    socket.on("refresh service provider data", refreshData);
    return () => socket.off("refresh service provider data", refreshData);
  }, [socket]);

  // ─── Leaves ─────────────────────────────────────────────────────────────

  // Approved leave covering `date` for an employee, if any
  const findLeave = (employeeId, date) =>
    leavesArray.find(
      leave =>
        leave?.status === "Approved" &&
        leave?.employee?._id === employeeId &&
        dayjs(date).isBetween(dayjs(leave?.startDate), dayjs(leave?.endDate), "day", "[]")
    );

  // Employees annotated with today's leave status (used by the employee pickers)
  const employeesWithLeave = useMemo(
    () =>
      employees.map(emp => {
        const leave = findLeave(emp._id, new Date());
        return { ...emp, isOnLeave: !!leave, leaveReason: leave?.leaveType || null };
      }),
    [employees, leavesArray]
  );

  // ─── Events ─────────────────────────────────────────────────────────────

  const visibleRange = useMemo(
    () => getVisibleRange(calendarDate, calendarView),
    [calendarDate, calendarView]
  );
  const viewPeriod = useMemo(
    () => getViewPeriod(calendarDate, calendarView),
    [calendarDate, calendarView]
  );
  const periodLabel = `${moment(viewPeriod.start).format("DD/MMM")} – ${moment(viewPeriod.end).format("DD/MMM/YYYY")}`;

  // Overlaps and working time are computed across all shifts so filtering never hides a problem
  const calendarEvents = useMemo(
    () => detectOverlaps(shifts.flatMap(s => expandShift(s, visibleRange.start, visibleRange.end))),
    [shifts, visibleRange]
  );

  const filteredEvents = useMemo(
    () =>
      calendarEvents.filter(
        e =>
          (!selectedShift || e.title === selectedShift) &&
          (!selectedEmployee || (e.employees || []).some(emp => emp._id === selectedEmployee))
      ),
    [calendarEvents, selectedShift, selectedEmployee]
  );

  const workload = useMemo(
    () => computeWorkload(calendarEvents, workRules),
    [calendarEvents, workRules]
  );

  const viewWeeks = useMemo(() => {
    const weeks = new Set();
    for (let d = moment(viewPeriod.start); d.isBefore(viewPeriod.end); d.add(1, "day")) {
      weeks.add(weekKey(d));
    }
    return weeks;
  }, [viewPeriod]);

  const workloadRows = useMemo(
    () =>
      workload.rows.filter(
        r => viewWeeks.has(r.week) && (!selectedEmployee || r.employee._id === selectedEmployee)
      ),
    [workload, viewWeeks, selectedEmployee]
  );
  const workloadIssueCount = workloadRows.filter(
    r => r.overMax || r.dailyViolations.length || r.restViolations.length
  ).length;

  const plannedHours = useMemo(() => {
    const map = {};
    workload.rows.forEach(r => {
      map[r.employee._id] = map[r.employee._id] || {};
      map[r.employee._id][r.week] = r.planned;
    });
    return map;
  }, [workload]);

  const updateRules = rules => {
    setWorkRules(rules);
    try {
      localStorage.setItem(RULES_STORAGE_KEY, JSON.stringify(rules));
    } catch {
      // per-browser convenience only
    }
  };

  const loadActuals = async () => {
    try {
      const res = await shiftApi.get("/actuals", {
        from: viewPeriod.start.toISOString(),
        to: viewPeriod.end.toISOString(),
        tz: getTimeZone()
      });
      const map = {};
      (res.data || []).forEach(({ employee, date, hours }) => {
        const wk = weekKey(date);
        map[employee] = map[employee] || {};
        map[employee][wk] = (map[employee][wk] || 0) + hours;
      });
      setActuals(map);
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  useEffect(() => {
    if (workloadOpen) loadActuals();
  }, [workloadOpen, viewPeriod]);

  // ─── Staffing suggestions ────────────────────────────────────────────────

  /**
   * Employees who could work `event`: not assigned, not on leave that day and not in an
   * overlapping shift, fewest planned hours that week first.
   */
  const rankSuggestions = (event, pool = calendarEvents, hoursFor) => {
    const assigned = new Set(ids(event.employees));
    const busy = new Set();
    pool.forEach(o => {
      if (o.cancelled || o._id === event._id) return;
      if (o.start < event.end && event.start < o.end) ids(o.employees).forEach(id => busy.add(id));
    });
    const week = weekKey(event.start);
    const hoursOf = hoursFor || (id => plannedHours[id]?.[week] || 0);
    return employees
      .filter(e => !assigned.has(e._id) && !busy.has(e._id) && !findLeave(e._id, event.start))
      .map(e => ({ employee: e, hours: hoursOf(e._id) }))
      .sort((a, b) => a.hours - b.hours);
  };

  // Suggestions for a swap request, computed from that request's own week
  const suggestionsForRequest = request => {
    const shift = shifts.find(s => s._id === request.shift?._id);
    if (!shift) return [];
    const day = moment(request.date, DATE_KEY);
    const pool = detectOverlaps(
      shifts.flatMap(s =>
        expandShift(
          s,
          day.clone().startOf("isoWeek").subtract(1, "day").toDate(),
          day.clone().endOf("isoWeek").add(1, "day").toDate()
        )
      )
    );
    const occurrence = pool.find(o => o._originalId === shift._id && o._dateKey === request.date);
    if (!occurrence) return [];
    const week = weekKey(occurrence.start);
    const withoutRequester = {
      ...occurrence,
      employees: (occurrence.employees || []).filter(e => e._id !== request.requester?._id)
    };
    return rankSuggestions(withoutRequester, pool, id => plannedHoursIn(pool, id, week)).filter(
      s => s.employee._id !== request.requester?._id
    );
  };

  // ─── Writes ─────────────────────────────────────────────────────────────

  const save = async (method, path, body) => {
    try {
      const res = await saveWithConflictCheck(method, path, body, t);
      if (res) {
        Success(res.data.successMessage);
        getAllShifts();
      }
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const setDayStaff = (event, employeeIds) =>
    save("put", `/occurrence/${event._originalId}`, {
      date: event._dateKey,
      employees: employeeIds
    });

  const setSeriesStaff = (event, employeeIds) =>
    save("put", `/update-employees/${event._originalId}`, { employees: employeeIds });

  // Drag & drop / resize: one-off shifts move as a whole, recurring shifts only on that day
  const moveOccurrence = ({ event, start, end }) => {
    if (event.cancelled) return;
    const from = moment(start);
    const to = moment(end);
    if (!to.isAfter(from)) return;
    const shift = event._shift;
    if (!isRecurringShift(shift)) {
      save("put", `/update/${shift._id}`, {
        startRecur: from.format(RECUR_FORMAT),
        endRecur: to.format(RECUR_FORMAT),
        start: from.toISOString(),
        end: to.toISOString()
      });
      return;
    }
    Modal.confirm({
      title: `${t("Move")} "${event.title}" ${t("only on")} ${moment(event.start).format("ddd DD/MMM")}?`,
      content: `${t("New time")}: ${from.format("ddd DD/MMM HH:mm")} – ${to.format("HH:mm")}. ${t("The rest of the shift stays as it is.")}`,
      okText: t("Move"),
      cancelText: t("Cancel"),
      onOk: () =>
        save("put", `/occurrence/${shift._id}`, {
          date: event._dateKey,
          start: from.toISOString(),
          end: to.toISOString()
        })
    });
  };

  const openAdd = initialValues => setAddState({ open: true, initialValues });

  // Clicking/dragging over empty calendar space opens "Add Shift" prefilled with that time
  const openAddForSlot = ({ start, end }) => {
    let from = dayjs(start);
    let to = dayjs(end);
    if (calendarView === Views.MONTH) {
      to = to.subtract(1, "day").hour(17).minute(0);
      from = from.hour(9).minute(0);
    }
    const daysOfWeek = [];
    if (!to.isSame(from, "day")) {
      for (let d = from.startOf("day"); !d.isAfter(to, "day"); d = d.add(1, "day")) {
        if (!daysOfWeek.includes(d.day())) daysOfWeek.push(d.day());
      }
    }
    openAdd({ range: [from, to], daysOfWeek: daysOfWeek.sort() });
  };

  // ─── Toolbar ─────────────────────────────────────────────────────────────

  const FilterMenu = [...new Set(shifts.map(s => s.title))].map(title => ({
    key: title,
    label: title
  }));

  const exportEvents = () =>
    filteredEvents.filter(e => e.start >= viewPeriod.start && e.start <= viewPeriod.end);

  const onExport = ({ key }) => {
    const events = exportEvents();
    const suffix = `${moment(viewPeriod.start).format("YYYY-MM-DD")}_${moment(viewPeriod.end).format("YYYY-MM-DD")}`;
    if (key === "ics") exportICS(events, `shifts_${suffix}.ics`);
    if (key === "csv") exportCSV(events, `shifts_${suffix}.csv`);
    if (key === "print" && !printShifts(events, `${t("Shift plan")} ${periodLabel}`)) {
      Error(t("Please allow pop-ups to print the shift plan"));
    }
  };

  const pendingRequests = swapRequests.filter(r => r.status === "Pending").length;

  // ─── Calendar Helpers ────────────────────────────────────────────────────

  const eventPropGetter = event => {
    const classes = ["calendarEvent"];
    if (event.cancelled) classes.push("shiftCancelled");
    if (isUnderstaffed(event)) classes.push("shiftUnderstaffed");
    return {
      className: classes.join(" "),
      style: {
        backgroundColor: event.colorCode || DEFAULT_SHIFT_COLOR,
        borderColor: event.colorCode || DEFAULT_SHIFT_COLOR,
        color: token.colorTextLightSolid
      }
    };
  };

  const planningContext = {
    t,
    view: calendarView,
    findLeave,
    workIssues: workload.issues,
    rankSuggestions,
    setDayStaff,
    setSeriesStaff
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <PlanningContext.Provider value={planningContext}>
      <div
        className="ShiftPlanning shift-planning"
        style={{
          "--sp-warning": token.colorWarning,
          "--sp-error": token.colorError,
          "--sp-on-color": token.colorTextLightSolid
        }}
      >
        <PageToolbar
          className="shift-planning__toolbar"
          filters={
            <>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={t("All employees")}
                value={selectedEmployee}
                onChange={value => setSelectedEmployee(value || null)}
                options={employees.map(e => ({ value: e._id, label: fullName(e) }))}
              />
              <div className="shift-planning__filter-buttons">
                <Dropdown
                  menu={{ items: FilterMenu, onClick: ({ key }) => setSelectedShift(key) }}
                  trigger={["click"]}
                >
                  <Button icon={<FilterOutlined />} className="shift-planning__filter-button">
                    <span className="shift-planning__ellipsis">{selectedShift || t("Filter")}</span>
                    <DownOutlined />
                  </Button>
                </Dropdown>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setSelectedShift(null);
                    setSelectedEmployee(null);
                  }}
                >
                  {t("Reset")}
                </Button>
              </div>
            </>
          }
          actions={
            <>
              <Badge count={pendingRequests} size="small">
                <Tooltip title={isMobile ? t("Requests") : undefined}>
                  <Button
                    icon={<SwapOutlined />}
                    aria-label={t("Requests")}
                    onClick={() => setRequestsOpen(true)}
                  >
                    {!isMobile && t("Requests")}
                  </Button>
                </Tooltip>
              </Badge>
              <Badge count={workloadIssueCount} size="small">
                <Tooltip title={isMobile ? t("Workload") : undefined}>
                  <Button
                    icon={<BarChartOutlined />}
                    aria-label={t("Workload")}
                    onClick={() => setWorkloadOpen(true)}
                  >
                    {!isMobile && t("Workload")}
                  </Button>
                </Tooltip>
              </Badge>
              <Tooltip title={isMobile ? t("Copy week") : undefined}>
                <Button
                  icon={<CopyOutlined />}
                  aria-label={t("Copy week")}
                  onClick={() => setCopyWeekOpen(true)}
                >
                  {!isMobile && t("Copy week")}
                </Button>
              </Tooltip>
              <Dropdown
                trigger={["click"]}
                menu={{
                  onClick: onExport,
                  items: [
                    { key: "ics", label: t("Calendar file (.ics)") },
                    { key: "csv", label: t("Excel / CSV") },
                    { key: "print", label: t("Print / PDF") }
                  ]
                }}
              >
                <Button icon={<ExportOutlined />} aria-label={t("Export")}>
                  {!isMobile && (
                    <>
                      {t("Export")} <DownOutlined />
                    </>
                  )}
                </Button>
              </Dropdown>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                className="shift-planning__add"
                onClick={() => openAdd(undefined)}
              >
                {t("Add Shift")}
              </Button>
            </>
          }
        />

        {/* Calendar: week/month grids scroll sideways inside their frame on phones */}
        <DnDCalendar
          className={`shift-planning__calendar is-${calendarView}`}
          localizer={localizer}
          events={filteredEvents}
          startAccessor="start"
          endAccessor="end"
          view={calendarView}
          onView={view => setCalendarView(view)}
          date={calendarDate}
          onNavigate={date => setCalendarDate(date)}
          style={{ height: isMobile ? "calc(100dvh - 220px)" : "75vh", minHeight: 480 }}
          views={[Views.MONTH, Views.WEEK, Views.DAY]}
          rtl={i18n.dir() === "rtl"}
          eventPropGetter={eventPropGetter}
          onSelectEvent={event => setEditTarget(event)}
          selectable
          onSelectSlot={openAddForSlot}
          resizable
          draggableAccessor={event => !event.cancelled}
          onEventDrop={moveOccurrence}
          onEventResize={moveOccurrence}
          components={calendarComponents}
          messages={calendarMessages(t, { noEventsInRange: t("No shifts in this period") })}
          formats={{
            timeGutterFormat: (date, culture, loc) => loc.format(date, "HH:mm", culture),
            eventTimeRangeFormat: ({ start, end }, culture, loc) =>
              `${loc.format(start, "HH:mm", culture)} – ${loc.format(end, "HH:mm", culture)}`,
            agendaTimeRangeFormat: ({ start, end }, culture, loc) =>
              `${loc.format(start, "HH:mm", culture)} – ${loc.format(end, "HH:mm", culture)}`
          }}
        />
        {shifts.length === 0 && (
          <Empty
            style={{ marginTop: 16 }}
            description={t("No shifts yet. Click or drag on the calendar to create one.")}
          />
        )}

        <AddShifts
          hideButton
          open={addState.open}
          initialValues={addState.initialValues}
          onClose={() => setAddState({ open: false, initialValues: undefined })}
          employees={employeesWithLeave}
          updateParent={getAllShifts}
        />

        {editTarget && (
          <UpdateShift
            employees={employeesWithLeave}
            data={editTarget._shift}
            occurrence={editTarget}
            showEventModal
            onClose={() => setEditTarget(null)}
            onDuplicate={openAdd}
            updateParent={getAllShifts}
          />
        )}

        <WorkloadDrawer
          open={workloadOpen}
          onClose={() => setWorkloadOpen(false)}
          rows={workloadRows}
          actuals={actuals}
          rules={workRules}
          onRulesChange={updateRules}
          periodLabel={periodLabel}
        />

        <SwapRequestsDrawer
          open={requestsOpen}
          onClose={() => setRequestsOpen(false)}
          requests={swapRequests}
          employees={employees}
          suggestionsFor={suggestionsForRequest}
          onChanged={() => {
            getSwapRequests();
            getAllShifts();
          }}
        />

        <CopyWeekModal
          open={copyWeekOpen}
          onClose={() => setCopyWeekOpen(false)}
          sourceDate={calendarDate}
          onCopied={getAllShifts}
        />
      </div>
    </PlanningContext.Provider>
  );
};
